import { useTranslation } from "@next-platform/client-common-core";
import useCanAttachRulebook from "@next-platform/client-common-core/rulebook/attach/useCanAttachRulebook";
import { Modal, ModalSize, Tabs } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo } from "react";

import RulebookAttachActions from "./RulebookAttachActions";
import RulebookAttachControls from "./RulebookAttachControls";
import RulebookAttachTargetContent from "./RulebookAttachTargetContent";
import useRulebookAttachTargets, { type RulebookAttachOwnerKind } from "./useRulebookAttachTargets";

export type RulebookAttachTargetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rulebookId: string;
  rulebookName: string;
  /** Called after a successful attach so callers can refresh counts/views. */
  onAttached?: () => void;
};

const isSubmitBlocked = (selected: unknown, alreadyAttached: boolean, blocked: boolean, attaching: boolean): boolean =>
  !selected || alreadyAttached || blocked || attaching;

/** Tab labels carry the loaded target counts (`Bots {n}` / `Personas {n}`) — plain labels while loading. */
const buildAttachTabItems = ({
  t,
  canAttachCharacters,
  canAttachPersonas,
  numCharacters,
  numPersonas,
}: {
  t: (key: string, values?: Record<string, unknown>) => string;
  canAttachCharacters: boolean;
  canAttachPersonas: boolean;
  numCharacters: number | null;
  numPersonas: number | null;
}): { name: string; label: string }[] => {
  const items: { name: string; label: string }[] = [];
  if (canAttachCharacters) {
    items.push({
      name: "characters",
      label: numCharacters === null ? t("rulebook:attach.tab.characters") : t("rulebook:attach.tab.botsCount", { count: numCharacters }),
    });
  }
  if (canAttachPersonas) {
    items.push({
      name: "personas",
      label: numPersonas === null ? t("rulebook:attach.tab.personas") : t("rulebook:attach.tab.personasCount", { count: numPersonas }),
    });
  }
  return items;
};

/**
 * "Attach to a character / persona" picker for a single Rulebook — opened from the explore card
 * ⋮ menu and the public profile page. One Rulebook per owner: attaching replaces the current one.
 */
const RulebookAttachTargetModal = ({ isOpen, onClose, rulebookId, rulebookName, onAttached }: RulebookAttachTargetModalProps) => {
  const { t } = useTranslation();
  const { canAttachCharacters, canAttachPersonas } = useCanAttachRulebook();
  const targetsState = useRulebookAttachTargets({ isOpen, rulebookId, onClose, canAttachCharacters, canAttachPersonas, onAttached });
  const {
    ownerKind,
    setOwnerKind,
    search,
    setSearch,
    targets,
    selectedTarget,
    isAlreadyAttached,
    isReplacing,
    selectedBlockedStatus,
    isSelectedBlocked,
    isAttaching,
    isLoading,
    isInitialError,
    isLoadMoreError,
    retry,
    hasMoreCharacters,
    loadMoreCharacters,
    isLoadingMoreCharacters,
    handleSelectTarget,
    handleSubmit,
    numCharacters,
    numPersonas,
  } = targetsState;

  const hasBothOwnerKinds = canAttachCharacters && canAttachPersonas;
  const tabItems = useMemo(
    () => buildAttachTabItems({ t, canAttachCharacters, canAttachPersonas, numCharacters, numPersonas }),
    [t, canAttachCharacters, canAttachPersonas, numCharacters, numPersonas],
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setOwnerKind(tab as RulebookAttachOwnerKind);
    },
    [setOwnerKind],
  );

  const isSubmitDisabled = isSubmitBlocked(selectedTarget, isAlreadyAttached, isSelectedBlocked, isAttaching);
  const submitLabel = isReplacing ? t("rulebook:attach.action.replace") : t("rulebook:attach.action.attach");

  return (
    <Modal
      title={t("rulebook:attach.title", { name: rulebookName })}
      open={isOpen}
      onOpenChange={onClose}
      dismissable={!isAttaching}
      size={ModalSize.MEDIUM}
      backdrop="blur"
      closeOnActions={false}
      data-testid="RulebookAttachTargetModal"
      description={t("rulebook:attach.description")}
      actions={
        <RulebookAttachActions
          isAttaching={isAttaching}
          isSubmitDisabled={isSubmitDisabled}
          submitLabel={submitLabel}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      }
    >
      {hasBothOwnerKinds ? <Tabs tabItems={tabItems} activeTab={ownerKind} onTabChange={handleTabChange} /> : null}
      <RulebookAttachControls
        ownerKind={ownerKind}
        search={search}
        setSearch={setSearch}
        isAlreadyAttached={isAlreadyAttached}
        selectedBlockedStatus={selectedBlockedStatus}
      />
      <RulebookAttachTargetContent
        ownerKind={ownerKind}
        rulebookId={rulebookId}
        search={search}
        targets={targets}
        selectedId={selectedTarget?.id ?? null}
        isAttaching={isAttaching}
        isLoading={isLoading}
        isInitialError={isInitialError}
        isLoadMoreError={isLoadMoreError}
        hasMoreCharacters={hasMoreCharacters}
        isLoadingMoreCharacters={isLoadingMoreCharacters}
        onLoadMore={loadMoreCharacters}
        onSelectTarget={handleSelectTarget}
        onRetry={retry}
      />
    </Modal>
  );
};

export default RulebookAttachTargetModal;
